import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useChatMachine } from '@/machines/useChatMachine';
import { MAX_MESSAGE_LENGTH } from '@/machines/chatMachine';
import { ZigBotFeedbackDialog } from './ZigBotFeedbackDialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ChatInput = () => {
    const { t } = useTranslation();
    const {
        messages,
        inputMessage,
        isStreaming,
        setInput,
        sendMessage,
        startNewConversation,
        prePrompts,
        prePromptsWereSet,
        sendPrePrompt,
    } = useChatMachine();

    const [feedbackOpen, setFeedbackOpen] = useState(false);

    const displayedPrePrompts = prePromptsWereSet ? prePrompts : [t('chat.prePrompt.default')];

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className="flex flex-col gap-3 pt-4 pb-6 flex-shrink-0 border-t light:border-gz-neutral-200 dark:border-gz-blue-500">
            <div data-onboarding="pre-prompts" className="flex flex-wrap gap-2 pb-2">
                {displayedPrePrompts.map((prompt, index) => (
                    <Button
                        key={index}
                        type="button"
                        variant="secondary"
                        size="xsmall"
                        onClick={() => sendPrePrompt(prompt)}
                        disabled={isStreaming}
                        data-track-click="AI Chat Pre-Prompt Clicked"
                        data-track-location="ai-chat"
                        data-track-button-name="ai-chat-pre-prompt"
                        data-track-prompt-text={prompt}
                    >
                        {prompt}
                    </Button>
                ))}
            </div>
            <div className="relative">
                <Textarea
                    value={inputMessage}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t('chat.input.placeholder')}
                    className="min-h-24 pr-16 resize-none text-white light:text-foreground dark:text-foreground light:border-gz-neutral-700 dark:border-gz-blue-500"
                    disabled={isStreaming}
                />
                <span className="absolute bottom-2 right-3 text-xs text-muted-foreground">
                    {inputMessage.length}
                    /
                    {MAX_MESSAGE_LENGTH}
                </span>
            </div>

            <div className="flex gap-2 justify-between items-end">
                <div className="flex flex-col justify-start h-full">
                    <Button
                        variant="link"
                        size="xsmall"
                        className="text-white light:text-foreground dark:text-foreground mt-[-1em] text-xs"
                        onClick={() => setFeedbackOpen(true)}
                        data-track-click="ZigBot Feedback Clicked"
                        data-track-location="ai-chat"
                        data-track-button-name="zigbot-feedback"
                    >
                        {t('chat.input.provideFeedback')}
                    </Button>
                    {messages.length > 0 && (
                        <Button
                            onClick={startNewConversation}
                            variant="outline"
                            className="rounded-full px-6 text-white light:text-foreground dark:text-foreground"
                            data-track-click="AI Chat New Chat Clicked"
                            data-track-location="ai-chat"
                            data-track-button-name="ai-chat-new-chat"
                        >
                            {t('chat.input.newChat')}
                        </Button>
                    )}
                </div>
                <Button
                    onClick={sendMessage}
                    disabled={!inputMessage.trim() || isStreaming}
                    className="ml-auto rounded-full px-6"
                    data-track-click="AI Chat Send Message Clicked"
                    data-track-location="ai-chat"
                    data-track-button-name="ai-chat-send-message"
                >
                    {t('chat.input.send')}
                </Button>
                <ZigBotFeedbackDialog isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
            </div>
        </div>
    );
};
